import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL, reportBulkDelete } from '../utils/formatters';

/**
 * Main-tab CRUD state + handlers.
 *
 * Owns:
 *   • add/edit dialog state (`formData`, `showCreateDialog`,
 *     `editingTransaction`, `submitting`) and its `handleSubmit`
 *   • delete-with-reason dialog state + `handleDelete` /
 *     `executeDelete` / `handleBulkDelete`
 *   • main-table bulk-select state (`selectedMain`) + toggles
 *
 * Behaviour is preserved verbatim from AdminTransactionsPage.jsx —
 * this hook is a pure re-parent.
 */
export function useTransactionCrud({ headers, fetchTransactions }) {
  // Add / Edit dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [formData, setFormData] = useState({
    investor_id: '',
    transaction_type: '',
    amount: '',
    transaction_date: new Date().toISOString().split('T')[0],
    reference: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Delete-with-reason
  const [deleteReasonOpen, setDeleteReasonOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [pendingDeleteAction, setPendingDeleteAction] = useState(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Main-table bulk selection
  const [selectedMain, setSelectedMain] = useState(new Set());

  const toggleSelectMain = (id) => {
    setSelectedMain(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleSelectAllMain = (ids) => {
    setSelectedMain(prev => prev.size === ids.length ? new Set() : new Set(ids));
  };

  const handleSubmit = async () => {
    if (!formData.investor_id || !formData.transaction_type || !formData.amount || !formData.transaction_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...formData, amount: parseFloat(formData.amount) };
      if (editingTransaction) {
        await axios.put(`${API_URL}/api/admin/investor-transactions/${editingTransaction.id}`, payload, { headers });
        toast.success('Transaction updated');
      } else {
        await axios.post(`${API_URL}/api/admin/investor-transactions`, payload, { headers });
        toast.success('Transaction created');
      }
      setShowCreateDialog(false);
      setEditingTransaction(null);
      setFormData({
        investor_id: '', transaction_type: '', amount: '',
        transaction_date: new Date().toISOString().split('T')[0],
        reference: '', notes: '',
      });
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save transaction');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (txn) => {
    setPendingDeleteAction({ type: 'single', txn });
    setDeleteReason('');
    setDeleteReasonOpen(true);
  };

  const handleBulkDelete = (selectedSet, clearFn) => {
    if (selectedSet.size === 0) return;
    setPendingDeleteAction({ type: 'bulk', ids: selectedSet, clearFn });
    setDeleteReason('');
    setDeleteReasonOpen(true);
  };

  const executeDelete = async () => {
    const action = pendingDeleteAction;
    if (!action) return;
    setDeleteReasonOpen(false);
    const reason = deleteReason.trim() || 'Manual deletion';

    if (action.type === 'single') {
      try {
        await axios.delete(`${API_URL}/api/admin/investor-transactions/${action.txn.id}?reason=${encodeURIComponent(reason)}`, { headers });
        toast.success('Deleted');
        fetchTransactions();
      } catch { toast.error('Delete failed'); }
    } else if (action.type === 'bulk') {
      setBulkDeleting(true);
      try {
        const r = await axios.post(`${API_URL}/api/admin/investor-transactions/bulk-delete`, {
          ids: Array.from(action.ids),
          reason,
        }, { headers });
        reportBulkDelete(toast, r.data);
        action.clearFn(new Set());
        fetchTransactions();
      } catch { toast.error('Bulk delete failed'); }
      finally { setBulkDeleting(false); }
    }
    setPendingDeleteAction(null);
  };

  const handleEdit = (txn) => {
    setEditingTransaction(txn);
    setFormData({
      investor_id: txn.investor_id,
      transaction_type: txn.transaction_type,
      amount: txn.amount.toString(),
      transaction_date: txn.transaction_date,
      reference: txn.reference || '',
      notes: txn.notes || '',
    });
    setShowCreateDialog(true);
  };

  return {
    // Add/Edit dialog
    showCreateDialog, setShowCreateDialog,
    editingTransaction, setEditingTransaction,
    formData, setFormData,
    submitting,
    handleSubmit, handleEdit,

    // Delete-with-reason
    deleteReasonOpen, setDeleteReasonOpen,
    deleteReason, setDeleteReason,
    pendingDeleteAction,
    bulkDeleting,
    handleDelete, handleBulkDelete, executeDelete,

    // Main-table bulk selection
    selectedMain, setSelectedMain,
    toggleSelectMain, toggleSelectAllMain,
  };
}
